import { useSdk } from './use-sdk';
import { storeToRefs, defineStore } from 'pinia';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    initialized: false,
    currentUser: null,
  }),
  actions: {
    async init () {
      const sdk = useSdk();
      this.currentUser = await sdk.currentUser();
      this.initialized = true;
    },
    async signin (data) { 
      const sdk = useSdk();
      const res = await sdk.signinWithEmail(data.email, data.password, data.totpToken);
      this.currentUser = await sdk.currentUser();
      this.initialized = true;
      return res;
    },
    async signout () {
      const sdk = useSdk();
      await sdk.signout();
      this.currentUser = await sdk.currentUser();
      this.initialized = false;
    },
  },
});

export function useAuth () {
  const { signin, signout, init } = useAuthStore();
  const sdk = useSdk();
  const SERVICE_NAME = 'authentication';

  async function signinWithToken (token) {
    return sdk.signinWithToken(token);
  }
  const getToken = () => sdk.token();
  const register = async (data, skipSignIn = true) => {
    await sdk.create('accounts', data);
    if (skipSignIn) return;
    const { email, password } = data;
    await signout();
    const account = await signin({ email, password });
    init();
    return account;
  };
  const accountExists = async (email) => {
    const result = await sdk.create(SERVICE_NAME, {
      action: 'checkUniqueIdentity',
      identityKey: 'email',
      identity: email,
    });
    const isUnique = result?.unique;
    return !isUnique;
  };
  const sendVerificationEmail = async (opts) => {
    const payload = Object.assign({
      action: 'sendVerificationEmail',
    }, opts);
    return sdk.create(SERVICE_NAME, payload);
  };
  const applyActionCode = async (opts) => {
    const payload = Object.assign({
      action: 'applyActionCode',
    }, opts);
    return sdk.create(SERVICE_NAME, payload);
  };

  // Add an isAuthenticated function that safely checks auth status
  const isAuthenticated = async () => {
    try {
      const store = useAuthStore();
      
      // If store is not initialized, try to initialize it
      if (!store.initialized) {
        try {
          await init();
        } catch (initError) {
          console.error('Failed to initialize auth store:', initError);
          return false;
        }
      }
      
      // Double-check that currentUser exists after initialization
      if (!store.currentUser) {
        // Try one more time with SDK directly
        try {
          const currentUser = await sdk.currentUser(true); // Force fetch
          return !!currentUser;
        } catch (sdkError) {
          console.error('Failed to fetch current user from SDK:', sdkError);
          return false;
        }
      }
      
      return !!store.currentUser;
    } catch (error) {
      console.error('Error checking authentication status:', error);
      return false;
    }
  };
  
  // Add a function to check if user is a patient
  const isPatient = () => {
    const store = useAuthStore();
    return !!store.currentUser?.apps?.includes('parmazip-patient');
  };

  return {
    ...storeToRefs(useAuthStore()),
    accountExists,
    applyActionCode,
    getToken,
    init,
    isAuthenticated,
    isPatient,
    register,
    sendVerificationEmail,
    signin,
    signinWithToken,
    signout,
  };
}

export function useCurrentUser () {
  return useAuth().currentUser;
}

// The useAuthStore is already exported at the top of the file
